import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import {
  ShieldCheck, LogOut, Users, Stethoscope, Calendar, Receipt,
  Ban, CheckCircle2, AlertTriangle, Eye, ArrowLeft, X, Loader2, KeyRound,
  Megaphone, Plus, Trash2, Edit2, Save,
} from "lucide-react";
import Logo from "../components/Logo";
import DentalChart from "../components/DentalChart";
import AdminDotCoins from "../components/AdminDotCoins";
import DotCoinIcon from "../components/DotCoinIcon";

const adminClient = () => {
  const token = localStorage.getItem("admin_token");
  return axios.create({ baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {} });
};

const statusStyles = {
  Active: "bg-green-100 text-green-700 border-green-200",
  Disabled: "bg-red-100 text-red-700 border-red-200",
  Overdue: "bg-amber-100 text-amber-700 border-amber-200",
};

const AdminDashboard = () => {
  const nav = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewingDoctor, setViewingDoctor] = useState(null);
  const [resetDoctor, setResetDoctor] = useState(null);
  const [showNews, setShowNews] = useState(false);
  const [showDotCoins, setShowDotCoins] = useState(false);

  const client = adminClient();

  const load = async () => {
    setLoading(true);
    try {
      const [d, s] = await Promise.all([
        client.get("/admin/doctors"),
        client.get("/admin/stats"),
      ]);
      setDoctors(d.data);
      setStats(s.data);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        localStorage.removeItem("admin_token");
        nav("/admin/login");
      }
    } finally { setLoading(false); }
  };
  useEffect(() => {
    if (!localStorage.getItem("admin_token")) { nav("/admin/login"); return; }
    load();
    // eslint-disable-next-line
  }, []);

  const setStatus = async (doctor, next) => {
    if (!window.confirm(`Change ${doctor.name}'s status to "${next}"?`)) return;
    try {
      const res = await client.put(`/admin/doctors/${doctor.id}/status`, { status: next });
      setDoctors((prev) => prev.map((d) => (d.id === doctor.id ? { ...d, ...res.data } : d)));
    } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo size="md" dark />
            <span className="inline-flex items-center gap-1.5 bg-white/10 text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded">
              <ShieldCheck size={12} /> Admin
            </span>
          </div>
          <button onClick={logout} className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-sm px-3 py-1.5 rounded">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {viewingDoctor ? (
          <DoctorPatientView doctor={viewingDoctor} onBack={() => setViewingDoctor(null)} client={client} />
        ) : (
          <>
            <h1 className="text-2xl font-bold text-slate-900 mb-1">Overview</h1>
            <div className="flex items-center justify-between mb-6">
              <p className="text-slate-500 text-sm">Manage doctor accounts & subscriptions</p>
              <div className="flex gap-2">
              <button
                onClick={() => setShowDotCoins(true)}
                data-testid="admin-open-dotcoins-btn"
                className="inline-flex items-center gap-2 bg-[#0F2A5F] hover:bg-[#0A1E4A] text-white text-sm font-semibold px-4 py-2 rounded-md"
              >
                <DotCoinIcon size={16} /> DotCoins
              </button>
              <button
                onClick={() => setShowNews(true)}
                className="inline-flex items-center gap-2 bg-[#14B8A6] hover:bg-[#0F8A7D] text-white text-sm font-semibold px-4 py-2 rounded-md"
              >
                <Megaphone size={14} /> Manage News
              </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard icon={Stethoscope} color="#0F2A5F" label="Total Doctors" value={stats?.total_doctors} />
              <StatCard icon={Users} color="#2ea55f" label="Total Patients" value={stats?.total_patients} />
              <StatCard icon={Calendar} color="#f5b800" label="Appointments" value={stats?.total_appointments} />
              <StatCard icon={Receipt} color="#14B8A6" label="Bills Generated" value={stats?.total_bills} />
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <StatusPill label="Active" value={stats?.active_doctors} style={statusStyles.Active} icon={CheckCircle2} />
              <StatusPill label="Overdue" value={stats?.overdue_doctors} style={statusStyles.Overdue} icon={AlertTriangle} />
              <StatusPill label="Disabled" value={stats?.disabled_doctors} style={statusStyles.Disabled} icon={Ban} />
            </div>

            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100"><h3 className="font-semibold text-slate-800">Doctors</h3></div>
              {loading ? (
                <div className="p-8 text-center text-slate-400 text-sm">Loading...</div>
              ) : doctors.length === 0 ? (
                <div className="p-8 text-center text-slate-500 text-sm">No doctors registered</div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200">
                      <th className="px-4 py-3">Doctor</th>
                      <th className="px-4 py-3">Phone</th>
                      <th className="px-4 py-3">Clinic</th>
                      <th className="px-4 py-3">Patients</th>
                      <th className="px-4 py-3">Appts</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {doctors.map((d) => (
                      <tr key={d.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-slate-800">{d.name}</div>
                          <div className="text-xs text-slate-500">{d.email}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{d.phone || "-"}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{d.clinic_name || "-"}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{d.patient_count || 0}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{d.appointment_count || 0}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${statusStyles[d.status] || statusStyles.Active}`}>{d.status || "Active"}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1.5">
                            {d.status !== "Active" && (
                              <button onClick={() => setStatus(d, "Active")} className="text-xs bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 px-2 py-1 rounded inline-flex items-center gap-1">
                                <CheckCircle2 size={11} /> Activate
                              </button>
                            )}
                            {d.status !== "Overdue" && (
                              <button onClick={() => setStatus(d, "Overdue")} className="text-xs bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 px-2 py-1 rounded inline-flex items-center gap-1">
                                <AlertTriangle size={11} /> Mark Overdue
                              </button>
                            )}
                            {d.status !== "Disabled" && (
                              <button onClick={() => setStatus(d, "Disabled")} className="text-xs bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 px-2 py-1 rounded inline-flex items-center gap-1">
                                <Ban size={11} /> Disable
                              </button>
                            )}
                            {d.status === "Overdue" && (
                              <button onClick={() => setViewingDoctor(d)} className="text-xs bg-slate-800 text-white hover:bg-slate-900 px-2 py-1 rounded inline-flex items-center gap-1">
                                <Eye size={11} /> View Data
                              </button>
                            )}
                            <button onClick={() => setResetDoctor(d)} className="text-xs bg-[#0F2A5F] text-white hover:bg-[#0A1E4A] px-2 py-1 rounded inline-flex items-center gap-1">
                              <KeyRound size={11} /> Reset Password
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
      {resetDoctor && (
        <AdminResetPasswordModal
          doctor={resetDoctor}
          client={client}
          onClose={() => setResetDoctor(null)}
        />
      )}

      {showDotCoins && <AdminDotCoins client={client} onClose={() => setShowDotCoins(false)} />}
      {showNews && <NewsManager client={client} onClose={() => setShowNews(false)} />}
    </div>
  );
};

const NewsManager = ({ client, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null or item or {} (new)
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await client.get("/admin/news");
      setItems(res.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const payload = {
        title: (editing.title || "").trim(),
        body: (editing.body || "").trim(),
        category: editing.category || "Info",
        is_active: editing.is_active !== false,
      };
      if (!payload.title || !payload.body) {
        alert("Title and body required");
        setBusy(false);
        return;
      }
      let res;
      if (editing.id) {
        res = await client.put(`/admin/news/${editing.id}`, payload);
        setItems((prev) => prev.map((x) => x.id === editing.id ? res.data : x));
      } else {
        res = await client.post("/admin/news", payload);
        setItems((prev) => [res.data, ...prev]);
      }
      setEditing(null);
    } catch (e) { alert(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this news item?")) return;
    try {
      await client.delete(`/admin/news/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch { alert("Failed"); }
  };

  const toggle = async (n) => {
    try {
      const res = await client.put(`/admin/news/${n.id}`, { is_active: !n.is_active });
      setItems((prev) => prev.map((x) => x.id === n.id ? res.data : x));
    } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl my-6">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Megaphone size={18} className="text-[#14B8A6]" />
            <h3 className="font-semibold text-slate-800">News & Announcements</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        <div className="px-6 py-5">
          {editing ? (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Title</label>
                <input value={editing.title || ""} onChange={(e) => setEditing((x) => ({ ...x, title: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Message</label>
                <textarea rows={4} value={editing.body || ""} onChange={(e) => setEditing((x) => ({ ...x, body: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
              </div>
              <div className="flex items-center gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Category</label>
                  <select value={editing.category || "Info"} onChange={(e) => setEditing((x) => ({ ...x, category: e.target.value }))} className="px-2 py-1.5 border border-slate-300 rounded text-sm">
                    <option>Info</option><option>Offer</option><option>Warning</option>
                  </select>
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-700 pt-5">
                  <input type="checkbox" checked={editing.is_active !== false} onChange={(e) => setEditing((x) => ({ ...x, is_active: e.target.checked }))} className="w-4 h-4 accent-[#14B8A6]" />
                  Active (visible to doctors)
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancel</button>
                <button onClick={save} disabled={busy} className="px-4 py-2 text-sm bg-[#0F2A5F] hover:bg-[#0A1E4A] disabled:opacity-70 text-white rounded-md font-medium inline-flex items-center gap-2">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  {editing.id ? "Update" : "Publish"}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex justify-end mb-3">
                <button onClick={() => setEditing({ title: "", body: "", category: "Info", is_active: true })} className="inline-flex items-center gap-1.5 bg-[#14B8A6] hover:bg-[#0F8A7D] text-white text-sm px-3 py-1.5 rounded">
                  <Plus size={13} /> New Announcement
                </button>
              </div>
              {loading ? (
                <div className="p-6 text-center text-slate-400 text-sm">Loading...</div>
              ) : items.length === 0 ? (
                <div className="p-8 text-center text-slate-500 text-sm">No news yet. Click "New Announcement" to publish one.</div>
              ) : (
                <ul className="divide-y divide-slate-100 max-h-[60vh] overflow-y-auto">
                  {items.map((n) => (
                    <li key={n.id} className="py-3 flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${n.category === "Offer" ? "bg-[#E6F7F5] text-[#0F8A7D]" : n.category === "Warning" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>{n.category}</span>
                          <div className="font-semibold text-slate-800 text-sm">{n.title}</div>
                          {!n.is_active && <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">Hidden</span>}
                        </div>
                        <div className="text-xs text-slate-600 mt-1 whitespace-pre-line line-clamp-2">{n.body}</div>
                        <div className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => toggle(n)} className="text-xs text-slate-500 hover:text-[#0F2A5F] px-2 py-1 rounded hover:bg-slate-100">
                          {n.is_active ? "Hide" : "Show"}
                        </button>
                        <button onClick={() => setEditing(n)} className="p-1.5 text-slate-400 hover:text-[#0F2A5F] rounded"><Edit2 size={14} /></button>
                        <button onClick={() => remove(n.id)} className="p-1.5 text-slate-400 hover:text-red-600 rounded"><Trash2 size={14} /></button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const AdminResetPasswordModal = ({ doctor, client, onClose }) => {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const gen = () => {
    // simple 12-char temp password
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#";
    let s = "";
    for (let i = 0; i < 12; i++) s += chars.charAt(Math.floor(Math.random() * chars.length));
    setPw(s);
    setConfirm(s);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 6) return setErr("Password must be at least 6 characters");
    if (pw !== confirm) return setErr("Passwords do not match");
    setBusy(true);
    try {
      await client.put(`/admin/doctors/${doctor.id}/password`, { password: pw });
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to reset");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <KeyRound size={18} className="text-[#0F2A5F]" />
            <h3 className="font-semibold text-slate-800">Reset Password</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        {done ? (
          <div className="px-6 py-6 text-center">
            <div className="w-14 h-14 rounded-full bg-green-100 text-green-600 mx-auto flex items-center justify-center mb-3">
              <CheckCircle2 size={28} />
            </div>
            <h4 className="font-semibold text-slate-800 mb-1">Password reset</h4>
            <p className="text-sm text-slate-500 mb-4">
              Share this password with <strong>{doctor.name}</strong> securely. Ask them to change it after signing in.
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded p-3 font-mono text-lg text-[#0F2A5F] break-all mb-4">
              {pw}
            </div>
            <button onClick={onClose} className="w-full bg-[#14B8A6] hover:bg-[#0F8A7D] text-white font-semibold py-2 rounded-md">
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-5 space-y-4">
            <div className="bg-slate-50 rounded p-3 text-sm">
              <div className="text-xs text-slate-500">Doctor</div>
              <div className="font-medium text-slate-800">{doctor.name}</div>
              <div className="text-xs text-slate-500">{doctor.email}</div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-slate-600">New password</label>
                <button type="button" onClick={gen} className="text-xs text-[#0F2A5F] hover:underline">Generate</button>
              </div>
              <input
                type="text"
                required
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="Minimum 6 characters"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0F2A5F]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Confirm password</label>
              <input
                type="text"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0F2A5F]"
              />
            </div>
            {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">{err}</div>}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancel</button>
              <button type="submit" disabled={busy} className="px-4 py-2 text-sm bg-[#0F2A5F] hover:bg-[#0A1E4A] disabled:opacity-70 text-white rounded-md font-medium inline-flex items-center gap-2">
                {busy && <Loader2 size={14} className="animate-spin" />}
                Reset Password
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, color, label, value }) => (
  <div className="bg-white rounded-lg border border-slate-200 p-4">
    <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: `${color}18`, color }}>
      <Icon size={20} />
    </div>
    <div className="text-2xl font-bold text-slate-900">{value ?? "..."}</div>
    <div className="text-xs text-slate-500 mt-0.5">{label}</div>
  </div>
);

const StatusPill = ({ label, value, style, icon: Icon }) => (
  <div className={`rounded-lg border p-4 ${style}`}>
    <div className="flex items-center gap-2">
      <Icon size={16} />
      <span className="text-sm font-semibold">{label}</span>
    </div>
    <div className="text-2xl font-bold mt-1">{value ?? "..."}</div>
  </div>
);

const DoctorPatientView = ({ doctor, onBack, client }) => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientData, setPatientData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await client.get(`/admin/doctors/${doctor.id}/patients`);
        setPatients(res.data);
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [doctor.id]);

  const openPatient = async (p) => {
    setSelectedPatient(p);
    setPatientData(null);
    try {
      const [rec, appts] = await Promise.all([
        client.get(`/admin/doctors/${doctor.id}/patients/${p.id}/dental-record`),
        client.get(`/admin/doctors/${doctor.id}/patients/${p.id}/appointments`),
      ]);
      setPatientData({ record: rec.data, appts: appts.data });
    } catch (e) { console.error(e); }
  };

  return (
    <div>
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4">
        <ArrowLeft size={14} /> Back to doctors
      </button>

      <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-md mb-5 flex items-start gap-2">
        <AlertTriangle size={16} className="mt-0.5" />
        <div className="text-sm">
          <strong>{doctor.name}</strong> is marked as <strong>Overdue</strong>. You can view (read-only) their patient records.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-1 bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-800 text-sm">
            Patients ({patients.length})
          </div>
          {loading ? (
            <div className="p-6 text-center text-slate-400 text-sm">Loading...</div>
          ) : patients.length === 0 ? (
            <div className="p-6 text-center text-slate-500 text-sm">No patients</div>
          ) : (
            <ul className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
              {patients.map((p) => (
                <li key={p.id}>
                  <button onClick={() => openPatient(p)} className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${selectedPatient?.id === p.id ? "bg-slate-50" : ""}`}>
                    <div className="text-sm font-medium text-slate-800">{p.name}</div>
                    <div className="text-xs text-slate-500">UHID {p.uhid} • {p.phone}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="lg:col-span-2">
          {!selectedPatient ? (
            <div className="bg-white rounded-lg border border-slate-200 p-10 text-center text-slate-500 text-sm">
              Select a patient to view records
            </div>
          ) : !patientData ? (
            <div className="bg-white rounded-lg border border-slate-200 p-10 text-center"><Loader2 size={20} className="animate-spin mx-auto text-slate-400" /></div>
          ) : (
            <div className="space-y-5">
              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <h3 className="font-semibold text-slate-800 mb-3">{selectedPatient.name}</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  <Field label="UHID" value={selectedPatient.uhid} />
                  <Field label="Phone" value={selectedPatient.phone} />
                  <Field label="Email" value={selectedPatient.email || "-"} />
                  <Field label="Age" value={selectedPatient.age || "-"} />
                  <Field label="Gender" value={selectedPatient.gender || "-"} />
                  <Field label="Address" value={selectedPatient.address || "-"} />
                </div>
              </div>

              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <h3 className="font-semibold text-slate-800 mb-3">Examination Chart (read-only)</h3>
                <ReadOnlyDentalChart data={patientData.record} />
              </div>

              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <h3 className="font-semibold text-slate-800 mb-3">Appointments ({patientData.appts.length})</h3>
                {patientData.appts.length === 0 ? (
                  <p className="text-sm text-slate-500">No appointments</p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {patientData.appts.map((a) => (
                      <li key={a.id} className="py-2 text-sm">
                        <span className="font-medium text-slate-800">{a.date} {a.time}</span>
                        <span className="text-slate-500"> • {a.reason || "General"} • {a.status}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value }) => (
  <div>
    <div className="text-xs text-slate-500">{label}</div>
    <div className="text-sm text-slate-800">{value}</div>
  </div>
);

const ReadOnlyDentalChart = ({ data }) => {
  const teeth = data?.teeth || [];
  if (teeth.length === 0) return <p className="text-sm text-slate-500">No dental record</p>;
  return (
    <div className="space-y-2">
      {teeth.map((t) => (
        <div key={t.tooth} className="flex flex-wrap items-center gap-2 border border-slate-200 rounded p-2">
          <span className="font-mono text-sm font-semibold text-slate-700 min-w-[3rem]">{t.tooth}</span>
          <div className="flex flex-wrap gap-1">
            {t.conditions?.map((c) => (
              <span key={c} className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{c}</span>
            ))}
          </div>
          {t.notes && <span className="text-xs text-slate-500 italic">— {t.notes}</span>}
        </div>
      ))}
    </div>
  );
};

export default AdminDashboard;
